import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

// Shared icons
function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ─── Booking Config Tab ─────────────────────────────────────

export function BookingConfigTab({ practiceId }) {
  const [bookingConfigs, setBookingConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    label: '', patient_type: 'new', appointment_type_id: '',
    reason_code: '', reason_text: '', slot_keywords: '', is_default: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ehrAppointmentTypes, setEhrAppointmentTypes] = useState([]);
  const [ehrReasonCodes, setEhrReasonCodes] = useState([]);
  const [promptPreview, setPromptPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    loadData();
    api.request(`/practices/${practiceId}/ehr/appointment-types`)
      .then(res => setEhrAppointmentTypes((res.data || []).filter(t => t.enabled)))
      .catch(() => {});
    api.request(`/practices/${practiceId}/ehr/reason-codes`)
      .then(res => setEhrReasonCodes((res.data || []).filter(r => r.enabled)))
      .catch(() => {});
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.request(`/practices/${practiceId}/voice/booking-configs`);
      setBookingConfigs(res.data || []);
    } catch (err) {
      console.error('Error loading booking configs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPromptPreview = async () => {
    try {
      const res = await api.request(`/practices/${practiceId}/voice/booking-configs/prompt-preview`);
      if (res.success) setPromptPreview(res.data.prompt_rules);
      setShowPreview(true);
    } catch (err) {
      console.error('Error loading prompt preview:', err);
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({ label: '', patient_type: 'new', appointment_type_id: '', reason_code: '', reason_text: '', slot_keywords: '', is_default: false });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (config) => {
    setEditing(config.id);
    setFormData({
      label: config.label || '', patient_type: config.patient_type || 'new',
      appointment_type_id: config.appointment_type_id || '', reason_code: config.reason_code || '',
      reason_text: config.reason_text || '', slot_keywords: config.slot_keywords || '',
      is_default: !!config.is_default
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSave = async () => {
    if (!formData.label.trim()) { setError('Label is required'); return; }
    if (!formData.appointment_type_id) { setError('Appointment Type ID is required'); return; }
    if (!formData.reason_code) { setError('Reason Code is required'); return; }
    if (!formData.reason_text.trim()) { setError('Reason Text is required'); return; }
    try {
      setSaving(true); setError(null);
      const body = {
        ...formData,
        appointment_type_id: parseInt(formData.appointment_type_id, 10),
        reason_code: parseInt(formData.reason_code, 10),
        is_default: formData.is_default ? 1 : 0
      };
      if (editing === 'new') {
        await api.request(`/practices/${practiceId}/voice/booking-configs`, { method: 'POST', body: JSON.stringify(body) });
      } else {
        await api.request(`/practices/${practiceId}/voice/booking-configs/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
      }
      const res = await api.request(`/practices/${practiceId}/voice/booking-configs`);
      setBookingConfigs(res.data || []);
      setSuccess('Booking config saved');
      setEditing(null);
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Delete this booking config?')) return;
    try {
      await api.request(`/practices/${practiceId}/voice/booking-configs/${configId}`, { method: 'DELETE' });
      setBookingConfigs(prev => prev.filter(c => c.id !== configId));
      setSuccess('Booking config deleted');
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      {error && <div style={s.errorMsg}>{error}</div>}
      {success && <div style={s.successMsg}>{success}</div>}

      <div style={s.infoBox}>
        <strong>Appointment Type Mapping:</strong> Configure how patient types (new/existing) map to booking parameters.
        Each config links a patient type to the correct appointment_type, reason code, and slot matching keywords from your PMS.
      </div>

      <div style={s.sectionHeader}>
        <h4 style={s.sectionHeading}>Booking Configs</h4>
        <button style={s.addButton} onClick={handleNew}>+ Add Config</button>
      </div>

      {editing && (
        <div ref={formRef} style={s.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={s.formGroup}>
              <label style={s.label}>Label <span style={s.req}>*</span></label>
              <input type="text" style={s.input} placeholder="e.g., New Patient - Telehealth" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Patient Type <span style={s.req}>*</span></label>
              <select style={s.input} value={formData.patient_type} onChange={(e) => setFormData({ ...formData, patient_type: e.target.value })}>
                <option value="new">New Patient</option>
                <option value="existing">Existing Patient</option>
                <option value="all">All</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Appointment Type ID <span style={s.req}>*</span></label>
              {ehrAppointmentTypes.length > 0 ? (
                <>
                  <select style={s.input} value={formData.appointment_type_id} onChange={(e) => setFormData({ ...formData, appointment_type_id: e.target.value })}>
                    <option value="">Select Appointment Type</option>
                    {ehrAppointmentTypes.map(t => (
                      <option key={t.id} value={t.ehr_type_id}>{t.name} (ID: {t.ehr_type_id})</option>
                    ))}
                  </select>
                  <p style={s.help}>Synced from EHR. Manage in EHR Sync tab.</p>
                </>
              ) : (
                <>
                  <input type="number" style={s.input} placeholder="e.g., 683" value={formData.appointment_type_id} onChange={(e) => setFormData({ ...formData, appointment_type_id: e.target.value })} />
                  <p style={s.help}>Integer ID from athenahealth. Sync appointment types in EHR Sync tab to use dropdown.</p>
                </>
              )}
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Reason Code <span style={s.req}>*</span></label>
              {ehrReasonCodes.length > 0 ? (
                <>
                  <select style={s.input} value={formData.reason_code} onChange={(e) => setFormData({ ...formData, reason_code: e.target.value })}>
                    <option value="">Select Reason Code</option>
                    {ehrReasonCodes.map(r => (
                      <option key={r.id} value={r.ehr_reason_id}>{r.reason_text} (ID: {r.ehr_reason_id})</option>
                    ))}
                  </select>
                  <p style={s.help}>Synced from EHR. Manage in EHR Sync tab.</p>
                </>
              ) : (
                <>
                  <input type="number" style={s.input} placeholder="e.g., 81" value={formData.reason_code} onChange={(e) => setFormData({ ...formData, reason_code: e.target.value })} />
                  <p style={s.help}>BackendRsnCode / ReasonID from athenahealth. Sync reason codes in EHR Sync tab to use dropdown.</p>
                </>
              )}
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Reason Text <span style={s.req}>*</span></label>
            <input type="text" style={s.input} placeholder="e.g., New Patient - Telehealth" value={formData.reason_text} onChange={(e) => setFormData({ ...formData, reason_text: e.target.value })} />
            <p style={s.help}>UserReasnText sent to Mendix when booking</p>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Slot Keywords</label>
            <input type="text" style={s.input} placeholder="e.g., New,Telemed" value={formData.slot_keywords} onChange={(e) => setFormData({ ...formData, slot_keywords: e.target.value })} />
            <p style={s.help}>Comma-separated keywords to match against slot AppointmentType from your PMS</p>
            {ehrAppointmentTypes.length > 0 && (
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#6b7280' }}>Suggestions: </span>
                {ehrAppointmentTypes.map(t => (
                  <button key={t.id} type="button" onClick={() => {
                    const current = formData.slot_keywords ? formData.slot_keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
                    if (!current.some(k => k.toLowerCase() === t.name.toLowerCase())) {
                      setFormData({ ...formData, slot_keywords: [...current, t.name].join(', ') });
                    }
                  }} style={{
                    padding: '2px 8px', borderRadius: '12px', border: '1px solid #d1d5db',
                    background: '#f9fafb', fontSize: '11px', color: '#374151', cursor: 'pointer'
                  }}>
                    + {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={s.checkRow}>
            <input type="checkbox" checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} />
            <label style={s.checkLabel}>Default config for this patient type</label>
          </div>
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Config'}</button>
          </div>
        </div>
      )}

      {bookingConfigs.length === 0 && !editing && (
        <p style={s.empty}>No booking configs yet. Click "Add Config" to map appointment types.</p>
      )}

      {bookingConfigs.map((config) => (
        <div key={config.id} style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <h4 style={s.cardTitle}>{config.label}</h4>
              <span style={s.cardSubtitle}>
                appointment_type: {config.appointment_type_id} | reason_code: {config.reason_code} | keywords: "{config.slot_keywords || ''}"
              </span>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{
                padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                backgroundColor: config.patient_type === 'new' ? '#dcfce7' : config.patient_type === 'existing' ? '#dbeafe' : '#f3f4f6',
                color: config.patient_type === 'new' ? '#166534' : config.patient_type === 'existing' ? '#1e40af' : '#374151'
              }}>
                {config.patient_type}
              </span>
              {config.is_default ? <span style={s.defaultBadge}>default</span> : null}
            </div>
          </div>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>Reason: "{config.reason_text}"</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={s.editBtn} onClick={() => handleEdit(config)}><EditIcon /> Edit</button>
            <button style={{ ...s.editBtn, color: '#dc2626' }} onClick={() => handleDelete(config.id)}><CloseIcon /> Delete</button>
          </div>
        </div>
      ))}

      {/* Prompt Preview */}
      {bookingConfigs.length > 0 && (
        <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
          <button onClick={loadPromptPreview} style={{
            padding: '6px 14px', borderRadius: '4px', border: '1px solid #d1d5db',
            background: '#fff', fontSize: '12px', fontWeight: '500', cursor: 'pointer', color: '#374151'
          }}>
            {showPreview ? 'Refresh Preview' : 'Preview AI Prompt Rules'}
          </button>
          {showPreview && promptPreview && (
            <pre style={{
              background: '#f9fafb', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb',
              fontSize: '11px', whiteSpace: 'pre-wrap', marginTop: '8px', maxHeight: '400px',
              overflow: 'auto', lineHeight: '1.5', color: '#374151'
            }}>
              {promptPreview}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Departments Tab ────────────────────────────────────────

export function DepartmentsTab({ practiceId }) {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '', athena_department_id: '', is_default: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.request(`/practices/${practiceId}/voice/departments`);
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Error loading departments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({ name: '', address: '', phone: '', athena_department_id: '', is_default: false });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (dept) => {
    setEditing(dept.id);
    setFormData({
      name: dept.name || '', address: dept.address || '', phone: dept.phone || '',
      athena_department_id: dept.athena_department_id || '', is_default: !!dept.is_default
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { setError('Department name is required'); return; }
    try {
      setSaving(true); setError(null);
      const body = {
        ...formData,
        athena_department_id: formData.athena_department_id ? parseInt(formData.athena_department_id, 10) : null,
        is_default: formData.is_default ? 1 : 0
      };
      if (editing === 'new') {
        await api.request(`/practices/${practiceId}/voice/departments`, { method: 'POST', body: JSON.stringify(body) });
      } else {
        await api.request(`/practices/${practiceId}/voice/departments/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
      }
      const res = await api.request(`/practices/${practiceId}/voice/departments`);
      setDepartments(res.data || []);
      setSuccess('Department saved');
      setEditing(null);
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (deptId) => {
    if (!window.confirm('Delete this department? Provider associations will be removed.')) return;
    try {
      await api.request(`/practices/${practiceId}/voice/departments/${deptId}`, { method: 'DELETE' });
      setDepartments(prev => prev.filter(d => d.id !== deptId));
      setSuccess('Department deleted');
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      {error && <div style={s.errorMsg}>{error}</div>}
      {success && <div style={s.successMsg}>{success}</div>}

      <div style={s.infoBox}>
        <strong>Office Locations:</strong> Configure physical department/locations within your practice.
        Departments are used to route patients to the correct office when booking appointments.
      </div>

      <div style={s.sectionHeader}>
        <h4 style={s.sectionHeading}>Departments</h4>
        <button style={s.addButton} onClick={handleNew}>+ Add Department</button>
      </div>

      {editing && (
        <div ref={formRef} style={s.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={s.formGroup}>
              <label style={s.label}>Name <span style={s.req}>*</span></label>
              <input type="text" style={s.input} placeholder="e.g., Main Office" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Athena Department ID</label>
              <input type="number" style={s.input} placeholder="e.g., 1" value={formData.athena_department_id} onChange={(e) => setFormData({ ...formData, athena_department_id: e.target.value })} />
              <p style={s.help}>Department ID from athenahealth</p>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Address</label>
              <input type="text" style={s.input} placeholder="123 Main St, City, ST 12345" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Phone</label>
              <input type="text" style={s.input} placeholder="(555) 123-4567" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>
          </div>
          <div style={s.checkRow}>
            <input type="checkbox" checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} />
            <label style={s.checkLabel}>Default department (used when patient doesn't specify a location)</label>
          </div>
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Department'}</button>
          </div>
        </div>
      )}

      {departments.length === 0 && !editing && (
        <p style={s.empty}>No departments configured. Click "Add Department" to add office locations.</p>
      )}

      {departments.map((dept) => (
        <div key={dept.id} style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <h4 style={s.cardTitle}>{dept.name}</h4>
              <span style={s.cardSubtitle}>
                {dept.address || 'No address'}{dept.phone ? ` | ${dept.phone}` : ''}{dept.athena_department_id ? ` | Athena ID: ${dept.athena_department_id}` : ''}
              </span>
            </div>
            {dept.is_default ? <span style={s.defaultBadge}>default</span> : null}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={s.editBtn} onClick={() => handleEdit(dept)}><EditIcon /> Edit</button>
            <button style={{ ...s.editBtn, color: '#dc2626' }} onClick={() => handleDelete(dept.id)}><CloseIcon /> Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Providers Tab ──────────────────────────────────────────

export function ProvidersTab({ practiceId }) {
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [bookingConfigs, setBookingConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const SPECIALTIES = [
    'Family Medicine', 'Internal Medicine', 'Pediatrics', 'Cardiology', 'Dermatology',
    'Endocrinology', 'Gastroenterology', 'Neurology', 'Obstetrics & Gynecology',
    'Oncology', 'Ophthalmology', 'Orthopedics', 'Otolaryngology (ENT)', 'Psychiatry',
    'Pulmonology', 'Radiology', 'Rheumatology', 'Urology', 'General Surgery',
    'Plastic Surgery', 'Emergency Medicine', 'Anesthesiology', 'Pathology',
    'Physical Medicine & Rehabilitation', 'Allergy & Immunology', 'Infectious Disease',
    'Nephrology', 'Hematology', 'Pain Management', 'Sports Medicine', 'Other'
  ];
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', display_name: '', specialty: '', email: '',
    athena_provider_id: '', npi: '', ai_prompt_rules: '', is_default: false,
    department_ids: [], booking_config_ids: [], nh_provider_id: '', telehealth_link: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const formRef = useRef(null);
  // Schedule state
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  // Note preferences state
  const [editingNotes, setEditingNotes] = useState(null);
  const [notePrefs, setNotePrefs] = useState({ hpi: '', ros: '' });
  const [savingNotes, setSavingNotes] = useState(false);
  const [previewingNote, setPreviewingNote] = useState(null);
  const [notePreview, setNotePreview] = useState('');

  useEffect(() => {
    loadData();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, deptsRes, configsRes] = await Promise.all([
        api.request(`/practices/${practiceId}/voice/providers`).catch(() => ({ data: [] })),
        api.request(`/practices/${practiceId}/voice/departments`).catch(() => ({ data: [] })),
        api.request(`/practices/${practiceId}/voice/booking-configs`).catch(() => ({ data: [] }))
      ]);
      setProviders(providersRes.data || []);
      setDepartments(deptsRes.data || []);
      setBookingConfigs(configsRes.data || []);
    } catch (err) {
      console.error('Error loading providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      first_name: '', last_name: '', display_name: '', specialty: '', email: '',
      athena_provider_id: '', npi: '', ai_prompt_rules: '', is_default: false,
      department_ids: [], booking_config_ids: [], nh_provider_id: ''
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (provider) => {
    setEditing(provider.id);
    setFormData({
      first_name: provider.first_name || '', last_name: provider.last_name || '',
      display_name: provider.display_name || '', specialty: provider.specialty || '',
      email: provider.email || '',
      athena_provider_id: provider.athena_provider_id || '', npi: provider.npi || '',
      ai_prompt_rules: provider.ai_prompt_rules || '', is_default: !!provider.is_default,
      department_ids: provider.department_ids || [], booking_config_ids: provider.booking_config_ids || [],
      nh_provider_id: provider.nh_provider_id || '',
      telehealth_link: provider.telehealth_link || ''
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First name and last name are required'); return;
    }
    try {
      setSaving(true); setError(null);
      const body = {
        ...formData,
        athena_provider_id: formData.athena_provider_id ? parseInt(formData.athena_provider_id, 10) : null,
        is_default: formData.is_default ? 1 : 0
      };
      if (editing === 'new') {
        await api.request(`/practices/${practiceId}/voice/providers`, { method: 'POST', body: JSON.stringify(body) });
      } else {
        await api.request(`/practices/${practiceId}/voice/providers/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
      }
      const res = await api.request(`/practices/${practiceId}/voice/providers`);
      setProviders(res.data || []);
      setSuccess('Provider saved');
      setEditing(null);
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Delete this provider? All associations and schedules will be removed.')) return;
    try {
      await api.request(`/practices/${practiceId}/voice/providers/${providerId}`, { method: 'DELETE' });
      setProviders(prev => prev.filter(p => p.id !== providerId));
      setSuccess('Provider deleted');
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  // Note preference handlers
  const handleEditNotes = async (providerId) => {
    try {
      const res = await api.request(`/practices/${practiceId}/providers/${providerId}/note-preferences`);
      const prefs = res.data?.preferences || [];
      setNotePrefs({
        hpi: prefs.find(p => p.note_type === 'hpi')?.prompt_template || '',
        ros: prefs.find(p => p.note_type === 'ros')?.prompt_template || '',
      });
      setEditingNotes(providerId);
      setNotePreview('');
    } catch (err) {
      setError('Failed to load note preferences: ' + err.message);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const providerId = editingNotes;
      for (const noteType of ['hpi', 'ros']) {
        if (notePrefs[noteType]?.trim()) {
          await api.request(`/practices/${practiceId}/note-preferences`, {
            method: 'PUT',
            body: JSON.stringify({ provider_id: providerId, note_type: noteType, prompt_template: notePrefs[noteType] }),
          });
        }
      }
      setSuccess('Note preferences saved');
      setEditingNotes(null);
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handlePreviewNote = async (noteType) => {
    setPreviewingNote(noteType);
    setNotePreview('Generating preview...');
    try {
      const provider = providers.find(p => p.id === editingNotes);
      const res = await api.request(`/practices/${practiceId}/note-preferences/preview`, {
        method: 'POST',
        body: JSON.stringify({
          note_type: noteType,
          prompt_template: notePrefs[noteType],
          provider_name: provider ? `${provider.first_name} ${provider.last_name}` : '',
        }),
      });
      setNotePreview(res.data?.preview || 'No preview generated');
    } catch (err) {
      setNotePreview('Preview failed: ' + err.message);
    } finally {
      setPreviewingNote(null);
    }
  };

  // Schedule handlers
  const handleEditSchedule = async (providerId) => {
    try {
      const res = await api.request(`/practices/${practiceId}/voice/providers/${providerId}/schedule`);
      setScheduleBlocks(res.data || []);
      setEditingSchedule(providerId);
    } catch (err) {
      setError(`Failed to load schedule: ${err.message}`);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true); setError(null);
      await api.request(`/practices/${practiceId}/voice/providers/${editingSchedule}/schedule`, {
        method: 'PUT', body: JSON.stringify({ blocks: scheduleBlocks })
      });
      setSuccess('Schedule saved');
      setEditingSchedule(null);
      const res = await api.request(`/practices/${practiceId}/voice/providers`);
      setProviders(res.data || []);
    } catch (err) {
      setError(`Failed to save schedule: ${err.message}`);
    } finally {
      setSavingSchedule(false);
    }
  };

  const addScheduleBlock = (dayOfWeek) => {
    setScheduleBlocks(prev => [...prev, { day_of_week: dayOfWeek, start_time: '09:00', end_time: '17:00', is_available: 1, notes: '' }]);
  };

  const removeScheduleBlock = (index) => {
    setScheduleBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const updateScheduleBlock = (index, field, value) => {
    setScheduleBlocks(prev => prev.map((block, i) => i === index ? { ...block, [field]: value } : block));
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      {error && <div style={s.errorMsg}>{error}</div>}
      {success && <div style={s.successMsg}>{success}</div>}

      <div style={s.infoBox}>
        <strong>Provider Management:</strong> Configure doctors/practitioners for this practice.
        First/Last name must match athenahealth ProviderFirstName/ProviderLastName exactly for slot matching.
        Link providers to departments and appointment types to control routing.
      </div>

      <div style={s.sectionHeader}>
        <h4 style={s.sectionHeading}>Providers</h4>
        <button style={s.addButton} onClick={handleNew}>+ Add Provider</button>
      </div>

      {editing && (
        <div ref={formRef} style={s.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={s.formGroup}>
              <label style={s.label}>First Name <span style={s.req}>*</span></label>
              <input type="text" style={s.input} placeholder="e.g., John" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
              <p style={s.help}>Must match athenahealth ProviderFirstName exactly</p>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Last Name <span style={s.req}>*</span></label>
              <input type="text" style={s.input} placeholder="e.g., Smith" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
              <p style={s.help}>Must match athenahealth ProviderLastName exactly</p>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Display Name</label>
              <input type="text" style={s.input} placeholder="e.g., Dr. Smith" value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })} />
              <p style={s.help}>How Emily refers to the provider in conversation</p>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Specialty</label>
              <select style={s.input} value={SPECIALTIES.includes(formData.specialty) ? formData.specialty : (formData.specialty ? '_custom' : '')} onChange={(e) => {
                if (e.target.value === '_custom') setFormData({ ...formData, specialty: '' });
                else setFormData({ ...formData, specialty: e.target.value });
              }}>
                <option value="">Select Specialty</option>
                {SPECIALTIES.map(sp => <option key={sp} value={sp}>{sp}</option>)}
                <option value="_custom">Custom (type below)</option>
              </select>
              {formData.specialty && !SPECIALTIES.includes(formData.specialty) && (
                <input type="text" style={{ ...s.input, marginTop: 4 }} placeholder="Enter custom specialty" value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} />
              )}
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Email</label>
              <input type="email" style={s.input} placeholder="e.g., dr.smith@practice.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Athena Provider ID</label>
              <input type="number" style={s.input} placeholder="e.g., 123" value={formData.athena_provider_id} onChange={(e) => setFormData({ ...formData, athena_provider_id: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>NPI</label>
              <input type="text" style={s.input} placeholder="10-digit NPI" value={formData.npi} onChange={(e) => setFormData({ ...formData, npi: e.target.value })} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>NH Provider ID</label>
              <input type="text" style={s.input} placeholder="Newton Health internal ID" value={formData.nh_provider_id} onChange={(e) => setFormData({ ...formData, nh_provider_id: e.target.value })} />
            </div>
          </div>

          <div style={s.formGroup}>
            <label style={s.label}>Telehealth Link</label>
            <input type="url" style={s.input} placeholder="https://doxy.me/drsmith" value={formData.telehealth_link} onChange={(e) => setFormData({ ...formData, telehealth_link: e.target.value })} />
            <span style={s.helpText}>Telehealth/Doxy link for this provider. Available in templates as {'{{telehealth_link}}'}</span>
          </div>

          {departments.length > 0 && (
            <div style={s.formGroup}>
              <label style={s.label}>Departments</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                {departments.map(dept => (
                  <label key={dept.id} style={s.checkOption}>
                    <input type="checkbox" checked={formData.department_ids.includes(dept.id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...formData.department_ids, dept.id]
                          : formData.department_ids.filter(id => id !== dept.id);
                        setFormData({ ...formData, department_ids: ids });
                      }} />
                    {dept.name}
                  </label>
                ))}
              </div>
              <p style={s.help}>Which office locations this provider works at</p>
            </div>
          )}

          {bookingConfigs.length > 0 && (
            <div style={s.formGroup}>
              <label style={s.label}>Appointment Types</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                {bookingConfigs.map(bc => (
                  <label key={bc.id} style={s.checkOption}>
                    <input type="checkbox" checked={formData.booking_config_ids.includes(bc.id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...formData.booking_config_ids, bc.id]
                          : formData.booking_config_ids.filter(id => id !== bc.id);
                        setFormData({ ...formData, booking_config_ids: ids });
                      }} />
                    {bc.label} ({bc.patient_type})
                  </label>
                ))}
              </div>
              <p style={s.help}>Which appointment types this provider handles</p>
            </div>
          )}

          <div style={s.formGroup}>
            <label style={s.label}>AI Prompt Rules</label>
            <textarea style={{ ...s.input, minHeight: '60px' }} placeholder="Custom rules for Emily when this provider is relevant (optional)"
              value={formData.ai_prompt_rules} onChange={(e) => setFormData({ ...formData, ai_prompt_rules: e.target.value })} />
            <p style={s.help}>Added to Emily's prompt when this provider is configured</p>
          </div>

          <div style={s.checkRow}>
            <input type="checkbox" checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} />
            <label style={s.checkLabel}>Default provider (suggested when patient doesn't request a specific provider)</label>
          </div>
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Provider'}</button>
          </div>
        </div>
      )}

      {providers.length === 0 && !editing && (
        <p style={s.empty}>No providers configured. Click "Add Provider" to add doctors/practitioners.</p>
      )}

      {providers.map((provider) => (
        <div key={provider.id} style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <h4 style={s.cardTitle}>{provider.display_name || `${provider.first_name} ${provider.last_name}`}</h4>
              {provider.specialty && (
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500, backgroundColor: '#ede9fe', color: '#5b21b6', marginBottom: 4 }}>
                  {provider.specialty}
                </span>
              )}
              <span style={s.cardSubtitle}>
                {provider.first_name} {provider.last_name}
                {provider.email ? ` | ${provider.email}` : ''}
                {provider.athena_provider_id ? ` | Athena ID: ${provider.athena_provider_id}` : ''}
              </span>
            </div>
            {provider.is_default ? <span style={s.defaultBadge}>default</span> : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {provider.department_ids?.length > 0 && (
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                Departments: {departments.filter(d => provider.department_ids.includes(d.id)).map(d => d.name).join(', ') || 'None'}
              </p>
            )}
            {provider.booking_config_ids?.length > 0 && (
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                Appt Types: {bookingConfigs.filter(bc => provider.booking_config_ids.includes(bc.id)).map(bc => bc.label).join(', ') || 'None'}
              </p>
            )}
            {provider.ai_prompt_rules && (
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                Rules: {provider.ai_prompt_rules.substring(0, 100)}{provider.ai_prompt_rules.length > 100 ? '...' : ''}
              </p>
            )}
          </div>
          {/* GH-27: per-provider feedback toggle. Practice-level reputation
              flag still gates feedback overall; this OFF skips just this
              provider's appointments. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '13px', color: '#374151' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} title="When OFF, no feedback request is sent after this provider's visits (regardless of practice-level setting).">
              <input
                type="checkbox"
                checked={provider.feedback_enabled !== 0}
                onChange={async (e) => {
                  const next = e.target.checked ? 1 : 0;
                  // Optimistic update
                  setProviders(prev => prev.map(p => p.id === provider.id ? { ...p, feedback_enabled: next } : p));
                  try {
                    await api.request(`/practices/${practiceId}/voice/providers/${provider.id}`, {
                      method: 'PUT',
                      body: JSON.stringify({ feedback_enabled: next })
                    });
                  } catch (err) {
                    // Rollback on failure
                    setProviders(prev => prev.map(p => p.id === provider.id ? { ...p, feedback_enabled: next ? 0 : 1 } : p));
                    setError('Failed to update feedback toggle: ' + err.message);
                  }
                }}
              />
              Allow Feedback
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button style={s.editBtn} onClick={() => handleEdit(provider)}><EditIcon /> Edit</button>
            <button style={s.editBtn} onClick={() => handleEditSchedule(provider.id)}><SettingsIcon /> Schedule</button>
            <button style={{ ...s.editBtn, color: '#7c3aed' }} onClick={() => handleEditNotes(provider.id)}>📝 Notes</button>
            <button style={{ ...s.editBtn, color: '#dc2626' }} onClick={() => handleDelete(provider.id)}><CloseIcon /> Delete</button>
          </div>

          {/* Inline schedule editor */}
          {editingSchedule === provider.id && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderTop: '1px solid #e5e7eb' }}>
              <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#111827' }}>Weekly Schedule</h5>
              <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6b7280' }}>
                Informational schedule for Emily's conversational awareness. Not a replacement for athenahealth slot availability.
              </p>
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((dayName, dayIndex) => {
                const dayBlocks = scheduleBlocks
                  .map((block, origIdx) => ({ ...block, origIdx }))
                  .filter(b => b.day_of_week === dayIndex);
                return (
                  <div key={dayIndex} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ width: '90px', fontSize: '13px', fontWeight: 500, color: '#374151', paddingTop: '6px' }}>{dayName}</span>
                    <div style={{ flex: 1 }}>
                      {dayBlocks.map((block) => (
                        <div key={block.origIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <input type="time" style={{ ...s.input, width: '130px', padding: '4px 8px' }} value={block.start_time}
                            onChange={(e) => updateScheduleBlock(block.origIdx, 'start_time', e.target.value)} />
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
                          <input type="time" style={{ ...s.input, width: '130px', padding: '4px 8px' }} value={block.end_time}
                            onChange={(e) => updateScheduleBlock(block.origIdx, 'end_time', e.target.value)} />
                          <button style={{ ...s.editBtn, color: '#dc2626', padding: '2px 8px', fontSize: '12px' }}
                            onClick={() => removeScheduleBlock(block.origIdx)}>Remove</button>
                        </div>
                      ))}
                      {dayBlocks.length === 0 && <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Off</span>}
                      <button style={{ ...s.editBtn, padding: '2px 8px', fontSize: '11px', marginTop: '2px' }}
                        onClick={() => addScheduleBlock(dayIndex)}>+ Add</button>
                    </div>
                  </div>
                );
              })}
              <div style={s.actions}>
                <button style={s.cancelBtn} onClick={() => setEditingSchedule(null)} disabled={savingSchedule}>Cancel</button>
                <button style={s.saveBtn} onClick={handleSaveSchedule} disabled={savingSchedule}>
                  {savingSchedule ? 'Saving...' : 'Save Schedule'}
                </button>
              </div>
            </div>
          )}

          {/* Inline note preferences editor */}
          {editingNotes === provider.id && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#faf5ff', borderRadius: '8px', borderTop: '2px solid #7c3aed' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#111827' }}>Clinical Note Preferences</h5>
              <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6b7280' }}>
                Customize how AI generates HPI and ROS notes for this provider. Leave blank to use practice defaults.
              </p>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>HPI Format Instructions</label>
                <textarea
                  value={notePrefs.hpi}
                  onChange={e => setNotePrefs({ ...notePrefs, hpi: e.target.value })}
                  placeholder="e.g., Use OLDCARTS format. Include pertinent negatives. Keep under 250 words."
                  rows={3}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
                />
                <button
                  onClick={() => handlePreviewNote('hpi')}
                  disabled={!!previewingNote || !notePrefs.hpi}
                  style={{ marginTop: '4px', padding: '4px 12px', fontSize: '12px', border: '1px solid #7c3aed', borderRadius: '4px', background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer' }}
                >
                  {previewingNote === 'hpi' ? 'Generating...' : 'Preview with sample data'}
                </button>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>ROS Format Instructions</label>
                <textarea
                  value={notePrefs.ros}
                  onChange={e => setNotePrefs({ ...notePrefs, ros: e.target.value })}
                  placeholder="e.g., Organize by organ system. State positive findings, pertinent negatives. Use standard abbreviations."
                  rows={3}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'vertical' }}
                />
                <button
                  onClick={() => handlePreviewNote('ros')}
                  disabled={!!previewingNote || !notePrefs.ros}
                  style={{ marginTop: '4px', padding: '4px 12px', fontSize: '12px', border: '1px solid #7c3aed', borderRadius: '4px', background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer' }}
                >
                  {previewingNote === 'ros' ? 'Generating...' : 'Preview with sample data'}
                </button>
              </div>

              {notePreview && (
                <div style={{ marginBottom: '12px', padding: '10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Preview</label>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{notePreview}</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={s.cancelBtn} onClick={() => setEditingNotes(null)} disabled={savingNotes}>Cancel</button>
                <button style={s.saveBtn} onClick={handleSaveNotes} disabled={savingNotes}>
                  {savingNotes ? 'Saving...' : 'Save Note Preferences'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Shared Styles ──────────────────────────────────────────

const s = {
  loading: { textAlign: 'center', padding: '40px', color: '#6b7280' },
  errorMsg: { background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  successMsg: { background: '#f0fdf4', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  infoBox: { padding: '12px 16px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', lineHeight: '1.6', color: '#1d4ed8', marginBottom: '16px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  sectionHeading: { margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' },
  addButton: { padding: '6px 16px', fontSize: '13px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 },
  card: { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', backgroundColor: '#fafafa', marginBottom: '12px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  cardTitle: { margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' },
  cardSubtitle: { fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' },
  defaultBadge: { padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, backgroundColor: '#fef3c7', color: '#92400e' },
  formGroup: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  req: { color: '#ef4444' },
  help: { margin: '4px 0 0 0', fontSize: '12px', color: '#9ca3af' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', marginBottom: '12px' },
  checkLabel: { fontSize: '13px', color: '#374151', cursor: 'pointer' },
  checkOption: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  editBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
  empty: { margin: 0, fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }
};
